import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import api from '../services/api';
import { log } from '../utils/logger';
import './Account.css';

const Account = () => {
  const { user, logout, verifyToken, loading: authLoading } = useContext(AuthContext);
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState({ type: '', message: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [creatingAdmin, setCreatingAdmin] = useState(false);
  const [showCreateAdmin, setShowCreateAdmin] = useState(false);
  const [profileForm, setProfileForm] = useState({
    username: '',
    email: ''
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [adminForm, setAdminForm] = useState({
    username: '',
    email: '',
    password: ''
  });

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/admin');
      return;
    }

    const fetchProfile = async () => {
      try {
        const response = await api.get('/account/profile');
        const loadedProfile = response.data || null;
        setProfile(loadedProfile);
        setProfileForm({
          username: loadedProfile?.username || user?.username || '',
          email: loadedProfile?.email || user?.email || ''
        });
      } catch (error) {
        log.error('Erro ao carregar perfil da conta:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [authLoading, user, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const showMessage = (type, message) => {
    setFeedback({ type, message });
    setTimeout(() => {
      setFeedback({ type: '', message: '' });
    }, 4500);
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAdminChange = (e) => {
    const { name, value } = e.target;
    setAdminForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const response = await api.put('/account/profile', {
        username: profileForm.username,
        email: profileForm.email
      });
      const updated = response.data?.user || null;
      if (updated) {
        setProfile((prev) => ({ ...prev, ...updated }));
      }
      await verifyToken();
      showMessage('success', response.data?.message || 'Perfil atualizado com sucesso.');
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Não foi possível atualizar o perfil.');
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setSavingPassword(true);
    try {
      const response = await api.put('/account/password', {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
        confirmPassword: passwordForm.confirmPassword
      });
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      showMessage('success', response.data?.message || 'Senha alterada com sucesso.');
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Não foi possível alterar a senha.');
    } finally {
      setSavingPassword(false);
    }
  };

  const handleCreateAdminSubmit = async (e) => {
    e.preventDefault();
    setCreatingAdmin(true);
    try {
      const response = await api.post('/account/admin-users', {
        username: adminForm.username,
        email: adminForm.email,
        password: adminForm.password
      });
      setAdminForm({ username: '', email: '', password: '' });
      setShowCreateAdmin(false);
      showMessage('success', response.data?.message || 'Administrador criado com sucesso.');
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Não foi possível criar o novo admin.');
    } finally {
      setCreatingAdmin(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="account-page">
        <div className="account-container">
          <div className="account-card">
            <p>Carregando conta...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="account-page">
      <div className="account-container">
        <section className="account-card">
          <h1>Minha conta</h1>
          <p className="account-subtitle">
            Gerencie seus dados de acesso e preferências da conta.
          </p>

          {feedback.message && (
            <div className={`account-message ${feedback.type}`}>{feedback.message}</div>
          )}

          <div className="account-grid">
            <div className="account-field">
              <span className="account-label">Nome de usuario</span>
              <strong>{user?.username || '-'}</strong>
            </div>
            <div className="account-field">
              <span className="account-label">E-mail</span>
              <strong>{user?.email || profile?.email || '-'}</strong>
            </div>
            <div className="account-field">
              <span className="account-label">Perfil</span>
              <strong>{user?.isAdmin ? 'Administrador' : 'Cliente'}</strong>
            </div>
            <div className="account-field">
              <span className="account-label">Status</span>
              <strong>{profile?.isActive === false ? 'Inativo' : 'Ativo'}</strong>
            </div>
          </div>

          <div className="account-sections">
            <section className="account-section">
              <h2>Editar perfil</h2>
              <form onSubmit={handleProfileSubmit} className="account-form">
                <div className="account-form-grid">
                  <label>
                    <span>Nome de usuário</span>
                    <input
                      type="text"
                      name="username"
                      value={profileForm.username}
                      onChange={handleProfileChange}
                      minLength={4}
                      maxLength={40}
                      required
                    />
                  </label>
                  <label>
                    <span>E-mail</span>
                    <input
                      type="email"
                      name="email"
                      value={profileForm.email}
                      onChange={handleProfileChange}
                      required
                    />
                  </label>
                </div>
                <button type="submit" className="account-btn" disabled={savingProfile}>
                  {savingProfile ? 'Salvando...' : 'Salvar perfil'}
                </button>
              </form>
            </section>

            <section className="account-section">
              <h2>Alterar senha</h2>
              <form onSubmit={handlePasswordSubmit} className="account-form">
                <div className="account-form-grid">
                  <label>
                    <span>Senha atual</span>
                    <input
                      type="password"
                      name="currentPassword"
                      value={passwordForm.currentPassword}
                      onChange={handlePasswordChange}
                      autoComplete="current-password"
                      required
                    />
                  </label>
                  <label>
                    <span>Nova senha</span>
                    <input
                      type="password"
                      name="newPassword"
                      value={passwordForm.newPassword}
                      onChange={handlePasswordChange}
                      minLength={8}
                      autoComplete="new-password"
                      required
                    />
                  </label>
                  <label>
                    <span>Confirmar nova senha</span>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={passwordForm.confirmPassword}
                      onChange={handlePasswordChange}
                      minLength={8}
                      autoComplete="new-password"
                      required
                    />
                  </label>
                </div>
                <button type="submit" className="account-btn secondary" disabled={savingPassword}>
                  {savingPassword ? 'Atualizando...' : 'Atualizar senha'}
                </button>
              </form>
            </section>

            {user?.isAdmin && (
              <section className="account-section account-section-admin">
                <h2>Administradores</h2>
                <p>Crie novos usuários com perfil de administrador do site.</p>
                {!showCreateAdmin ? (
                  <button
                    type="button"
                    className="account-btn admin"
                    onClick={() => setShowCreateAdmin(true)}
                  >
                    Criar novo admin
                  </button>
                ) : (
                  <form onSubmit={handleCreateAdminSubmit} className="account-form">
                    <div className="account-form-grid">
                      <label>
                        <span>Usuário admin</span>
                        <input
                          type="text"
                          name="username"
                          value={adminForm.username}
                          onChange={handleAdminChange}
                          minLength={4}
                          maxLength={40}
                          required
                        />
                      </label>
                      <label>
                        <span>E-mail admin</span>
                        <input
                          type="email"
                          name="email"
                          value={adminForm.email}
                          onChange={handleAdminChange}
                          required
                        />
                      </label>
                      <label>
                        <span>Senha provisória</span>
                        <input
                          type="password"
                          name="password"
                          value={adminForm.password}
                          onChange={handleAdminChange}
                          minLength={8}
                          required
                        />
                      </label>
                    </div>
                    <div className="account-inline-actions">
                      <button type="submit" className="account-btn admin" disabled={creatingAdmin}>
                        {creatingAdmin ? 'Criando...' : 'Confirmar novo admin'}
                      </button>
                      <button
                        type="button"
                        className="account-btn ghost"
                        onClick={() => setShowCreateAdmin(false)}
                        disabled={creatingAdmin}
                      >
                        Cancelar
                      </button>
                    </div>
                  </form>
                )}
              </section>
            )}
          </div>

          <div className="account-actions">
            <Link to="/noticias" className="account-btn">
              Ver noticias
            </Link>
            <Link to="/contato" className="account-btn secondary">
              Falar com o escritorio
            </Link>
            <button type="button" className="account-btn danger" onClick={handleLogout}>
              Sair
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Account;
